import { useState } from 'react'
import { useWindowDimensions, View } from 'react-native'
import { colors } from '@ca/ui/colors'
import ChipButton from '@ca/ui/elements/ChipButton'
import type { MeasurementOvalNormalizedFrame } from '@ca/ui/features/measurement/Oval'
import {
  doesOvalOverlapTopBand,
  resolveOvalLayout,
} from '@ca/ui/features/measurement/Oval'
import { MESSAGES_HEIGHT } from '@ca/ui/features/measurement/Messages/styles'
import Typography from '@ca/ui/typography'

export const OVAL_SCALE_DEFAULT = 1
export const OVAL_SCALE_MIN = 0.6
export const OVAL_SCALE_MAX = 2.2
export const OVAL_SCALE_STEP = 0.05

const OVAL_SCALE_PRESETS = [0.8, 1, 1.25, 1.5, 1.75, 2] as const

const formatScale = (scale: number) => `×${Number(scale.toFixed(2))}`

const clampScale = (scale: number) =>
  Math.min(OVAL_SCALE_MAX, Math.max(OVAL_SCALE_MIN, scale))

const snapScale = (scale: number) => {
  const snapped = Math.round(scale / OVAL_SCALE_STEP) * OVAL_SCALE_STEP
  return Number(clampScale(snapped).toFixed(2))
}

const isPresetActive = (preset: number, scale: number) =>
  Math.abs(preset - scale) < OVAL_SCALE_STEP / 2

type Props = {
  scale: number
  ovalFrame?: MeasurementOvalNormalizedFrame
  onChange: (scale: number) => void
}

/**
 * Debug-build overlay for stretching the measurement oval without waiting on
 * a wide-FOV device. Lets us check the messages fade against a large oval.
 */
export function OvalScaleDebugControl({ scale, ovalFrame, onChange }: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const { width, height } = useWindowDimensions()
  const layout = resolveOvalLayout(width, height, ovalFrame, scale)
  const isOverlapping = layout
    ? doesOvalOverlapTopBand(layout, MESSAGES_HEIGHT)
    : false
  const isScaled = !isPresetActive(OVAL_SCALE_DEFAULT, scale)
  const accentColor =
    isScaled || isOverlapping ? colors.amber[300] : undefined

  const handleNudge = (delta: number) => {
    onChange(snapScale(scale + delta))
  }

  return (
    <View className="absolute left-3 top-3" pointerEvents="box-none">
      <View className="items-start gap-2">
        <ChipButton
          label="Oval"
          value={formatScale(scale)}
          color={accentColor}
          onPress={() => setIsOpen((open) => !open)}
        />
        {isOpen && (
          <View className="w-64 overflow-hidden rounded-xl bg-black/80 p-3">
            <View className="mb-2 flex-row items-center justify-between">
              <Typography variant="XS" className="text-white">
                Scale
              </Typography>
              <Typography
                variant="XS"
                className={isOverlapping ? 'text-amber-300' : 'text-white/60'}
              >
                {isOverlapping ? 'Overlap' : 'Clear'}
              </Typography>
            </View>
            <View className="mb-2 flex-row items-center justify-between gap-2">
              <ChipButton
                value="−"
                onPress={() => handleNudge(-OVAL_SCALE_STEP)}
              />
              <Typography
                variant="SM"
                className="flex-1 text-center text-white"
              >
                {formatScale(scale)}
              </Typography>
              <ChipButton
                value="+"
                onPress={() => handleNudge(OVAL_SCALE_STEP)}
              />
            </View>
            <View className="flex-row flex-wrap gap-2">
              {OVAL_SCALE_PRESETS.map((preset) => {
                const isActive = isPresetActive(preset, scale)
                return (
                  <ChipButton
                    key={preset}
                    value={formatScale(preset)}
                    color={isActive ? colors.amber[300] : undefined}
                    onPress={() => onChange(preset)}
                  />
                )
              })}
            </View>
          </View>
        )}
      </View>
    </View>
  )
}
